import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewContainerRef,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { MapDrawComponent } from "@components/map/components/menu/map-draw/map-draw.component";
import { MapExtentComponent } from "@components/map/components/menu/map-extent/map-extent.component";
import { MapFullscreenComponent } from "@components/map/components/menu/map-fullscreen/map-fullscreen.component";
import { MapHomeComponent } from "@components/map/components/menu/map-home/map-home.component";
import { MapLayersComponent } from "@components/map/components/menu/map-layers/map-layers.component";
import { MapMeasureComponent } from "@components/map/components/menu/map-measure/map-measure.component";
import { MapSelectComponent } from "@components/map/components/menu/map-select/map-select.component";
import { MapComponent as MapComponent_1 , MapComponent } from "@components/map/map.component";
import { Subject } from "rxjs";
import { IMapExtentConfig } from "~app/common/components/map/components/menu/map-extent/map-extent-config";
import { IKartutsnitt } from "~app/common/components/map/components/menu/map-extent/map-extent.models";

import { IMapComponent, MapExtent } from "~app/common/components/map/map.models";
import {
    IFilterParams,
    IMapDrawLayer,
    IMapGeometry,
    IMapGeometryPoint,
    IMapGeometryWicket,
    IMapSettings,
    IRenderParams
} from "~app/common/components/map/map.settings";
import { IMapQueryResult } from "~app/common/components/map/services/map-query.service";
import { Subtyp1, Subtyp2, Subtyp3 } from "~app/common/components/map/symbols/kartgeometri.constants";
import {
    Arbetsordertyper,
    Avverkningsgrupper,
    KartaFeaturelayers,
    KartgeometriKategori,
    Produktionsstatus
} from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { KartgeometriPopupComponent } from "~app/features/map/popups/kartgeometri-popup/kartgeometri-popup.component";
import { IKartgeometri, Kartgeometri } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { IArbetsorderAtgarder } from "~app/features/planera/model/IArbetsorderAtgarder";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { AtgardPlaneringsstatusService } from "~app/features/planera/services/atgard-planeringsstatus.service";
import { SearchQuery } from "~app/features/transport/destineringsomraden/models/dso-models";
import { DsoService } from "~app/features/transport/destineringsomraden/services/dso.service";
import { AddArbetsorderKartutsnittCommand } from "../commands/AddArbetsorderKartutsnittCommand";
import { DeleteArbetsorderKartutsnittCommand } from "../commands/DeleteArbetsorderKartutsnittCommand";
import { UpdateArbetsorderKartutsnittCommand } from "../commands/UpdateArbetsorderKartutsnittCommand";
import { ListEvents } from "../events/list-events";
import { MapEvents } from "../events/map-events";
import { IStatusFilter } from "../model/IStatusFilter";
import { ArbetsorderWrapper, IArbetsorderMetadata } from "../planera-detail/planera-detail-models";
import { ArbetsorderKartSymbolService } from "../services/arbetsorder-kart-symbol.service";
import { Kartgeometrityper } from "../services/karta.service";
import { PlaneraAoGrotPopupComponent } from "./planera-ao-grot-popup/planera-ao-grot-popup.component";
import { PlaneraAoPopupComponent } from "./planera-ao-popup/planera-ao-popup.component";
import { PlaneraAtgardGrotPopupComponent } from "./planera-atgard-grot-popup/planera-atgard-grot-popup.component";
import { PlaneraAtgardPopupComponent } from "./planera-atgard-popup/planera-atgard-popup.component";
import { PlaneraKartaEventService } from "./planera-karta-event.service";
import { PlaneraKartaIcons } from "./planera-karta-icons";

@Component({
    selector: "rask-planera-karta",
    templateUrl: "./planera-karta.component.html",
    styleUrls: ["./planera-karta.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MapComponent_1,
        MapHomeComponent,
        MapSelectComponent,
        MapDrawComponent,
        MapExtentComponent,
        MapMeasureComponent,
        MapFullscreenComponent,
        MapLayersComponent
    ]
})
export class PlaneraKartaComponent implements OnInit, OnChanges, OnDestroy {
    @Input() arbetsordrar: any[];
    currentAoExtent: MapExtent;
    editMode: boolean;
    extentConfig: IMapExtentConfig;
    filter = new Subject<IFilterParams>();
    @Input() filterArbetsorderStatus: IStatusFilter;
    @ViewChild("infoAo") infoAo: ElementRef;
    map: IMapComponent;
    mapReady: boolean;
    @ViewChild(MapComponent, { read: ViewContainerRef }) mapRef: ViewContainerRef;
    mapsettings: IMapSettings;
    newGeometryIdCounter = -1;
    refresh = new Subject<IRenderParams>();

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("selected") selectedArbetsorder: IArbetsorderDetail;
    selectedGeometry: IMapGeometry;
    selectedMetadata: IArbetsorderMetadata;
    settings = new Subject<IMapSettings>();

    private readonly AOLAYER_ID = "ao";

    private readonly ATGLAYER_ID = "atg";

    private readonly DPLAYER_ID = "dp";
    private readonly arbetsorderKartSymbolService = inject(ArbetsorderKartSymbolService);
    private readonly atgardPlaneringsstatusService = inject(AtgardPlaneringsstatusService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly dsoService = inject(DsoService);
    private readonly offset = 15;
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);
    private readonly planeraKartaEvents: IBridgeEvent[] = [];

    private defaultExtent: MapExtent;
    private isAtgarderLayerLoaded = false;
    private isAtgarderUrvalLoaded = false;
    private isStinaLayerLoaded = false;
    private panThrottle: any;
    private throttle: any;

    allLayersReady(map) {
        this.map = map;
        this.mapReady = true;
        this.filterArbetsorderUrval(this.selectedArbetsorder?.guid);
    }

    canEdit() {
        return this.editMode
            && ObjectUtils.hasValue(this.selectedArbetsorder)
            && !this.selectedArbetsorder?.isExternArbetsorder
            && !this.atgardPlaneringsstatusService.isNotYetTillgangliga(this.selectedArbetsorder?.atgarder)
            && this.HasValidProduktionsstatus()
            && !this.selectedArbetsorder.koordinaterVerifierade;
    }

    clickAO(geometry: IMapGeometry) {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }
        const component = geometry.tag.typ === Arbetsordertyper.Grotskotning ? PlaneraAoGrotPopupComponent : PlaneraAoPopupComponent;
        this.clickGeometry(geometry, component, geometry.tag);
    }

    clickAtgard(geometry: IMapGeometry) {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }
        const data = geometry.tag;
        data.selectedArbetsorder = this.selectedArbetsorder;
        const component = geometry.tag.atgard.avverkningsgrupp === Avverkningsgrupper.Grotskotning ? PlaneraAtgardGrotPopupComponent : PlaneraAtgardPopupComponent;
        this.clickGeometry(geometry, component, data);
    }

    clickDestineringsomradeFeature(queryResult: IMapQueryResult[], isCtrl = false) {
        if (queryResult?.length > 0 && queryResult[0].geometries?.length > 0) {
            const arbetsordrar = this.map.getGeometriesByGeometry(this.AOLAYER_ID, queryResult[0].geometries[0]);
            const aoIdList = arbetsordrar.map(ao => ao.id);
            if (aoIdList.length === 0) {
                return;
            }
            this.selectArbetsordrarWithinDestineringsomrade(aoIdList, isCtrl);
        }
    }

    clickDrivningspunkt(geometry: IMapGeometry) {
        if (this.map.isPopupBlocked()) {
            return;
        }

        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }

        this.map.closeWidgets();

        if (this.selectedArbetsorder.kartgeometrier.some(x => x.isEditing)) {
            // eslint-disable-next-line sonarjs/no-ignored-return
            this.selectedArbetsorder.kartgeometrier.map(x => x.isEditing = false);
            this.renderDrivpunkter(this.selectedArbetsorder);
        }

        const data = geometry?.tag ?
            {
                geometri: geometry.tag,
                mapGeometry: geometry,
                kategori: KartgeometriKategori.Arbetsorder,
                eventService: this.planeraKartaEventService,
                layer: this.mapsettings.drawLayers.find(x => x.id === this.DPLAYER_ID),
                canEdit: this.canEdit()
            } : null;
        this.clickGeometry(geometry, KartgeometriPopupComponent, data);
    }

    clickGeometry(geometry: IMapGeometry, popupComponent, popupData: any) {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }
        this.selectedGeometry = geometry;
        this.map.showPopupOnGeometry(popupComponent, geometry, popupData)?.subscribe(c => {
            this.renderDrivpunkter(this.selectedArbetsorder);
        });
    }

    extentRemoved(extent: IKartutsnitt) {
        const command = new DeleteArbetsorderKartutsnittCommand(extent.guid, this.selectedArbetsorder.id);
        this.planeraKartaEventService.emit(MapEvents.arbetsorderExtentUpdated, command);
    }

    extentUpdated(extent: IKartutsnitt) {
        if (extent.guid) {
            const command = new UpdateArbetsorderKartutsnittCommand(extent, this.selectedArbetsorder.id);
            this.planeraKartaEventService.emit(MapEvents.arbetsorderExtentUpdated, command);
        } else {
            extent.guid = GuidUtils.newGuid();
            const command = new AddArbetsorderKartutsnittCommand(this.selectedArbetsorder.id, extent);
            this.planeraKartaEventService.emit(MapEvents.arbetsorderExtentUpdated, command);
        }
    }

    geometryCreated(geometry: IMapGeometryWicket) {
        const kartGeometri = new Kartgeometri(Kartgeometrityper.Drivningspunkt);
        kartGeometri.id = this.newGeometryIdCounter--;
        kartGeometri.geometri = geometry.wkt;
        const data = {
            geometri: kartGeometri,
            mapGeometry: geometry,
            kategori: KartgeometriKategori.Arbetsorder,
            eventService: this.planeraKartaEventService,
            canEdit: this.editMode,
            layer: this.mapsettings.drawLayers.find(l => l.id === this.DPLAYER_ID),
            isNew: true
        };

        const obs = this.map.showPopupOnGeometry(KartgeometriPopupComponent, geometry, data);
        obs?.subscribe(g => {
            this.renderDrivpunkter(this.selectedArbetsorder);
        });
        return obs;
    }

    geometryDeleted(geometries: IMapGeometry[]): boolean {
        const tags = geometries.map(g => this.getGeometryTag(g.id, this.DPLAYER_ID));
        tags.forEach(tag => {
            this.planeraKartaEventService.emit(MapEvents.geometriRemoved, tag);
        });
        this.map.closePopups();
        return true;
    }

    hoverArbetsorder(geometry: IMapGeometry, isMouseOver = true) {
        this.planeraKartaEventService.emit(MapEvents.mouseOverArbetsorder, {
            id: geometry?.id,
            beteckning: geometry?.tag?.beteckning,
            isMouseover: isMouseOver
        });
    }

    hoverAtgard(geometry: IMapGeometry, isMouseOver = true) {
        this.planeraKartaEventService.emit(MapEvents.mouseOverAtgard, {
            guid: geometry.tag?.atgard?.guid,
            isMouseover: isMouseOver
        });
    }

    layerReady(map, layerid) {
        this.map = map;
        switch (layerid) {
            case this.AOLAYER_ID:
                this.renderGeometries();
                break;
            case this.DPLAYER_ID:
                this.renderDrivpunkter(this.selectedArbetsorder);
                break;
            case KartaFeaturelayers.AtgarderSelection:
                this.isAtgarderUrvalLoaded = true;
                this.filterAtgarderUrval(this.selectedArbetsorder?.guid);
                break;
            case KartaFeaturelayers.Atgarder:
                this.isAtgarderLayerLoaded = true;
                this.filterAtgarderOnArbetsorderStatus(this.filterArbetsorderStatus, this.selectedArbetsorder?.guid);
                break;
            case KartaFeaturelayers.StinaKorstrak:
            case KartaFeaturelayers.StinaGrotpunkter:
            case KartaFeaturelayers.StinaHansyn:
            case KartaFeaturelayers.StinaEgenritadeObjekt:
                this.isStinaLayerLoaded = true;
                break;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.selectedArbetsorder
            && changes.selectedArbetsorder.previousValue?.id === changes.selectedArbetsorder.currentValue?.id
            && !changes.selectedArbetsorder.firstChange) {
            return;
        }
        if (this.mapReady) {
            this.renderGeometries();
        }
        if (changes.selectedArbetsorder) {
            this.planeraKartaEventService.emit(MapEvents.arbetsorderUpdated, this.selectedArbetsorder);
        }
        this.setCurrentAoExtent();
    }

    ngOnDestroy(): void {
        this.planeraKartaEventService.unsubscribeMany(this.planeraKartaEvents);
        this.planeraKartaEvents.splice(0);
        this.defaultExtent = null;
    }

    ngOnInit(): void {
        this.editMode = this.authenticationService.canEdit();
        this.registerEvents();
        const aoIcons = this.arbetsorderKartSymbolService.getAllIconsWithText().map(x => (
            {
                id: x.getUniqueId(),
                iconUrlPath: x.getImgSrc(),
                iconUrlPathSelected: x.getImgSrcSelected(),
                // Ikonerna är 22x22 och 30x22 (trianglar).
                // Någon skalningsbugg finns i ArcGis som gör att vi måste öka storleken med 1px
                // Detta tar bort suddigheten
                width: x.shape === "TRIANGLE" ? "31px" : "23px",
                height: "23px"
            }
        ));

        this.mapsettings = {
            id: "ppl",
            mapVerticalStart: 220,
            center: [
                17.9531,
                59.3565
            ],
            zoom: 0,
            onLayerReady: (map, layerid) => this.layerReady(map, layerid),
            onAllLayersReady: (map) => this.allLayersReady(map),
            onMapClick: (mouse, hitlist) => this.onMapClick(mouse, hitlist),
            onMapPan: (map: IMapComponent, zoomlevel: number, scale: number, center: any, extent: any, stationary?: boolean) =>
                this.onMapPan(map, zoomlevel, scale, center, extent, stationary),
            baselayers: [
                {
                    id: "metriabakgrund",
                    visible: true
                },
                {
                    id: "metriaorto",
                    visible: true
                }
            ],
            featurelayers: [
                {
                    id: KartaFeaturelayers.Infrarodorto,
                    visible: false
                },
                { id: KartaFeaturelayers.Satellitbilder },
                { id: KartaFeaturelayers.Drone },
                { id: KartaFeaturelayers.MetriaFastighetsgranser },
                { id: KartaFeaturelayers.SmittadZonSjv },
                { id: KartaFeaturelayers.Naturhansyn },
                { id: KartaFeaturelayers.Restriktioner },
                { id: KartaFeaturelayers.VerkGemAdminGrans },
                { id: KartaFeaturelayers.VerkGemLLD },
                { id: KartaFeaturelayers.VerkGemLKF },
                { id: KartaFeaturelayers.VerkGemFastigheter },
                { id: KartaFeaturelayers.VerkGemSamfalligheter },
                { id: KartaFeaturelayers.VerkGemFaktaExternVindkraft },
                { id: KartaFeaturelayers.VerkGemFaktaBelastningar },
                { id: KartaFeaturelayers.FornKulturminnen },
                { id: KartaFeaturelayers.Vagar },
                { id: KartaFeaturelayers.VFS_PlaneradVag },
                { id: KartaFeaturelayers.Laserprodukter },
                { id: KartaFeaturelayers.AllmannaYtorLinjerPunkter },
                { id: KartaFeaturelayers.RaskMottagningsplatser },
                {
                    id: KartaFeaturelayers.Destineringsomraden,
                    visible: true,
                    onClickFeature: (geometries, mouse, isCtrl) => {
                        this.clickDestineringsomradeFeature(geometries, isCtrl);
                        return false;
                    }
                },
                {
                    id: KartaFeaturelayers.Atgarder,
                    hideInLayerList: true,
                    visible: true,
                    minScale: 120000
                },
                {
                    id: KartaFeaturelayers.AtgarderSelection,
                    hideInLayerList: true,
                    visible: true,
                    minScale: 120000
                },
                {
                    id: KartaFeaturelayers.StinaKorstrak,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.StinaGrotpunkter,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.StinaHansyn,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.StinaEgenritadeObjekt,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.SKS_Avverkningsanmalningar,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.SKS_UtfordAvverkning,
                    visible: false
                },
                {
                    id: KartaFeaturelayers.Renskotselomraden,
                    visible: false
                }
            ],
            scaleBar: true,
            measure: true,
            coords: true,
            zoomButtons: true,
            drawLayers: [
                {
                    id: this.DPLAYER_ID,
                    title: "Drivningspunkter",
                    hideInLayerList: true,
                    visible: false,
                    geometries: [],
                    selectable: true,
                    // showGeometriesMenu: true,
                    readonly: true,
                    onClick: (mouse, geometry) => this.clickDrivningspunkt(geometry),
                    onGeometryDeleted: (geometries) => this.geometryDeleted(geometries),
                    drawOptions: [
                        {
                            title: `Ny drivningspunkt`,
                            type: "point",
                            callback: (g) => this.geometryCreated(g),
                            onBeforeDraw: () => true,
                            hideMenuOnDraw: true
                        }
                    ]
                } as IMapDrawLayer,
                {
                    id: this.AOLAYER_ID,
                    title: "Arbetsordrar",
                    visible: true,
                    readonly: true,
                    selectable: true,
                    hideInLayerList: true,
                    onClick: (mouseevent, geometry) => this.clickAO(geometry),
                    onHoverIn: (geometry, mouseevent) => this.hoverArbetsorder(geometry),
                    onHoverOut: (geometry, mouseevent) => this.hoverArbetsorder(geometry, false),
                    onSelect: (selected) => this.planeraKartaEventService.emit(MapEvents.selectMulti, {
                        items: selected,
                        selected: true
                    }),
                    onDeselect: (deselected) => {
                        this.planeraKartaEventService.emit(MapEvents.selectMulti, {
                            items: deselected,
                            selected: false
                        });
                        this.filterToSelected(false);
                    }
                } as IMapDrawLayer,
                {
                    id: this.ATGLAYER_ID,
                    title: "Åtgärder",
                    visible: true,
                    readonly: true,
                    selectable: true,
                    hideInLayerList: true,
                    minScale: 5000,
                    onClick: (mouse, geometry) => this.clickAtgard(geometry),
                    onHoverIn: (geometry, mouseevent) => this.hoverAtgard(geometry),
                    onHoverOut: (geometry, mouseevent) => this.hoverAtgard(geometry, false)
                } as IMapDrawLayer
            ],
            icons: [
                PlaneraKartaIcons.Kojplats,
                PlaneraKartaIcons.Avlagg,
                PlaneraKartaIcons.Larmpunkt,
                ...aoIcons
            ]

        } as IMapSettings;
        this.setupExtent();
    }

    onMapClick(mouse, hitlist) {
        if (!this.map.isPopupBlocked() && this.map.isWidgetsActive(["map-layers"])) {
            this.map.closeWidgets();
        }
    }

    onMapPan(map, zoomlevel: number, scale: number, center: any, extent: any, stationary: boolean) {
        clearTimeout(this.panThrottle);
        this.panThrottle = setTimeout(() => {
            if (stationary === true && this.map) {
                if (this.selectedArbetsorder) {
                    this.planeraKartaEventService.emit(MapEvents.mapPan,
                        {
                            arbetsorderId: this.selectedArbetsorder?.id,
                            minX: extent?.xmin,
                            minY: extent?.ymin,
                            maxX: extent?.xmax,
                            maxY: extent?.ymax
                        });
                } else {
                    this.defaultExtent = new MapExtent(extent?.xmin, extent?.ymin, extent?.xmax, extent?.ymax);
                }
            }
        }, 100);
    }

    refreshLayer(layerId: string) {
        this.refresh.next({ layerId: layerId });
    }

    selectArbetsordrarWithinDestineringsomrade(aoIdList: any[], isCtrl: boolean): void {
        if (isCtrl === false) {
            this.map?.clearSelection();
        }
        this.map.selectGeometries(this.AOLAYER_ID, aoIdList, true);
    }

    setupExtent() {
        if (!this.extentConfig) {
            this.extentConfig = {
                arbetsorderId: undefined,
                canEdit: undefined
            };
        }
        this.extentConfig.arbetsorderId = this.selectedArbetsorder?.id;
        this.extentConfig.canEdit = this.editMode && this.selectedArbetsorder?.produktionsstatus !== Produktionsstatus.ProduktionGodkand
            && !this.selectedArbetsorder?.isExternArbetsorder;
    }

    private HasValidProduktionsstatus() {
        if (!this.selectedArbetsorder?.produktionsstatus) {
            return false;
        }

        const validStatus = [
            Produktionsstatus.ProduktionEjPaborjad,
            Produktionsstatus.ProduktionGodkand,
            Produktionsstatus.ProduktionPagar
        ];

        return validStatus.includes(this.selectedArbetsorder.produktionsstatus);
    }

    private addSelectedArbetsorderToArbetsorderList() {
        if (this.selectedArbetsorder) {
            let aoIndexInList = -1;
            if (this.arbetsordrar) {
                aoIndexInList = this.arbetsordrar?.findIndex(x => x.id === this.selectedArbetsorder.id);
            }
            if (aoIndexInList === -1) {
                if (!this.arbetsordrar) {
                    this.arbetsordrar = [];
                }
                this.arbetsordrar.push(this.selectedArbetsorder);
            } else {
                // Om AO redan finns så plockar vi ut AOn från den positionen
                // Sen stoppar vi in den nya/uppdaterade AOn längst ner
                // Detta gör att AOn alltid ritas ut sist vilket blir att den alltid syns
                this.arbetsordrar.splice(aoIndexInList, 1);
                this.arbetsordrar.push(this.selectedArbetsorder);
            }
        }
    }

    private clearDrivningspunkter() {
        this.mapsettings.drawLayers.find(x => x.id === this.DPLAYER_ID).geometries = [];
        this.refreshLayer(this.DPLAYER_ID);
    }

    private filterArbetsorderUrval(aoGuid: string = undefined) {
        if (ObjectUtils.isNullOrUndefined(this.map) || !this.isStinaLayerLoaded) {
            return;
        }

        if (ObjectUtils.isNullOrUndefined(aoGuid)) {
            this.hideStinaLayers(true);
        } else {
            this.hideStinaLayers(false);
        }

        const filterExpression = `WORKORDERGUID LIKE '${aoGuid}'`;
        this.filter.next({
            layerId: KartaFeaturelayers.StinaKorstrak,
            filterExpression: filterExpression
        });
        this.filter.next({
            layerId: KartaFeaturelayers.StinaGrotpunkter,
            filterExpression: filterExpression
        });
        this.filter.next({
            layerId: KartaFeaturelayers.StinaHansyn,
            filterExpression: filterExpression
        });
        this.filter.next({
            layerId: KartaFeaturelayers.StinaEgenritadeObjekt,
            filterExpression: filterExpression
        });
    }

    private filterAtgarderOnArbetsorderStatus(status: IStatusFilter, excludeAoGuid?: any) {
        if (ObjectUtils.isNullOrUndefined(this.map) || !this.isAtgarderLayerLoaded) {
            return;
        }

        let filterExpression = "";
        const layerId = KartaFeaturelayers.Atgarder;

        if (status?.arbetsorderAvslutad !== true) {
            filterExpression = `ArbetsorderStatus <> '${Produktionsstatus.ProduktionGodkand}'`;
        }
        if (!ObjectUtils.isNullOrUndefined(excludeAoGuid)) {
            const conditionOperator = filterExpression.length > 0 ? " and " : "";
            filterExpression += `${conditionOperator}Arbetsorder_GUID <> '${excludeAoGuid}'`;
        }
        this.map.filterLayer(layerId, filterExpression);
    }

    private filterAtgarderUrval(aoGuid: any = "") {
        if (ObjectUtils.isNullOrUndefined(this.map) || !this.isAtgarderUrvalLoaded) {
            return;
        }

        if (!aoGuid && !this.map.isLayerVisible(KartaFeaturelayers.AtgarderSelection)) {
            return;
        }
        if (!aoGuid && this.map.isLayerVisible(KartaFeaturelayers.AtgarderSelection)) {
            this.map.hideFeatureLayer(KartaFeaturelayers.AtgarderSelection);
            return;
        }

        const filterExpression = `Arbetsorder_GUID = '${aoGuid}'`;
        this.filter.next({
            layerId: KartaFeaturelayers.AtgarderSelection,
            sublayerId: "Avverkningsåtgärder",
            filterExpression: filterExpression
        });

        if (!this.map.isLayerVisible(KartaFeaturelayers.AtgarderSelection)) {
            this.map.showFeatureLayer(KartaFeaturelayers.AtgarderSelection);
        }
    }

    private filterAvlaggKojplatsLarmpunkt(kartgeometri: IKartgeometri) {
        return (kartgeometri.subtyp1 === Subtyp1.drivningspunkt &&
            (kartgeometri.subtyp2 === Subtyp2.avlagg ||
                (kartgeometri.subtyp2 === Subtyp2.ovrigDrivningspunkt && kartgeometri.subtyp3 === Subtyp3.ovrigOverfartKojplats) ||
                (kartgeometri.subtyp2 === Subtyp2.ovrigDrivningspunkt && kartgeometri.subtyp3 === Subtyp3.ovrigOverfartLarmpunkt) ||
                kartgeometri.isEditing));
    }

    private filterDestineringsomradeHelgrot(): void {
        if (ObjectUtils.isNullOrUndefined(this.map)) {
            return;
        }
        const query = {
            // Handelssortimentet Helgrot har id 127
            handelssortimentIdList: [127]
        } as SearchQuery;
        this.dsoService.search(query).subscribe(destineringsomrade => {
            const guidFilters = destineringsomrade.map(d => `GUID = '${d.guid}'`);
            const filterExpression = guidFilters.join(" OR ");
            this.filter.next({
                layerId: KartaFeaturelayers.Destineringsomraden,
                sublayerId: KartaFeaturelayers.Destineringsomradeytor,
                filterExpression: filterExpression
            });
        });
    }

    /**
     * Show all arbetsorder geometries or only those who are selected
     *
     * @param value Show only selected geometries or not
     */
    private filterToSelected(value: boolean): void {
        if (value) {
            this.map.filterGeometries([this.AOLAYER_ID], this.map.getSelectedGeometryIds(this.AOLAYER_ID));
        } else {
            this.map.filterGeometries([this.AOLAYER_ID], this.arbetsordrar.map(ao => ao.id));
        }

        this.refreshLayer(this.AOLAYER_ID);
    }

    private generateArbetsorderGeometry(ao: any): IMapGeometryPoint {
        const iconInfo = this.arbetsorderKartSymbolService.getIconInfoForArbetsorder(ao);
        if (!ao.northing || !ao.easting) {
            // Kartan krashar om det är undefined
            // Placerar in AO ute i vattnet öster om Gävle
            ao.northing = 6750000;
            ao.easting = 800000;
        }
        return {
            id: ao.id,
            northing: ao.northing,
            easting: ao.easting,
            iconid: iconInfo.getUniqueId(),
            tag: ao,
            selected: false,
            visible: true
        };
    }

    private getAtgard(atg, ao) {
        const addedOffset = (ao.easting === atg.easting && ao.northing === atg.northing) ? this.offset : 0;
        return ({
            id: atg.guid,
            northing: atg.northing + addedOffset,
            easting: atg.easting + addedOffset,
            namn: atg.namn,
            raw: atg,
            ao: ao
        });
    }

    private getDrivningspunktIcon(drivningspunkt: IKartgeometri) {
        if (drivningspunkt.isEditing) {
            return null;
        }

        switch (this.getDrivningspunktTyp(drivningspunkt)) {
            case "Avlägg": return PlaneraKartaIcons.Avlagg.id;
            case "Larmpunkt": return PlaneraKartaIcons.Larmpunkt.id;
            case "Kojplats": return PlaneraKartaIcons.Kojplats.id;
        }
        return undefined;
    }

    private getDrivningspunktLabel(drivningspunkt: IKartgeometri) {
        return `${this.getDrivningspunktTyp(drivningspunkt)} ${drivningspunkt.karttext ?? ""}`;
    }

    private getDrivningspunktTyp(drivningspunkt: IKartgeometri) {
        return drivningspunkt.subtyp3 ? drivningspunkt.subtyp3 : drivningspunkt.subtyp2;
    }

    private getGeometryTag(geometryId: any, layerId: string) {
        return this.mapsettings.drawLayers.find(l => l.id === layerId)
            .geometries.find(g => g.id === geometryId).tag;
    }

    private gotoArbetsordrar(): void {
        if (ObjectUtils.isNullOrUndefined(this.selectedArbetsorder)) {
            if (ObjectUtils.isNullOrUndefined(this.defaultExtent)) {
                this.map?.gotoAll([this.AOLAYER_ID]);
            } else {
                this.map?.gotoExtent(this.defaultExtent);
            }
        } else {
            this.gotoSelectedArbetsorder();
        }
    }

    private gotoSelectedArbetsorder() {
        const ext = this.selectedMetadata?.extent;
        if (ext) {
            this.map?.gotoExtentDeprecated(ext.minX,
                ext.minY,
                ext.maxX,
                ext.maxY);
        }
    }

    private hideStinaLayers(hideInLayerList: boolean) {
        this.mapsettings.featurelayers.find(x => x.id === KartaFeaturelayers.StinaKorstrak).hideInLayerList = hideInLayerList;
        this.mapsettings.featurelayers.find(x => x.id === KartaFeaturelayers.StinaGrotpunkter).hideInLayerList = hideInLayerList;
        this.mapsettings.featurelayers.find(x => x.id === KartaFeaturelayers.StinaHansyn).hideInLayerList = hideInLayerList;
        this.mapsettings.featurelayers.find(x => x.id === KartaFeaturelayers.StinaEgenritadeObjekt).hideInLayerList = hideInLayerList;
        this.settings.next(this.mapsettings);
    }

    private registerEvents() {
        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(ListEvents.mouseOverArbetsorder, (e: any) => {
            if (e.isMouseover) {
                const layer = this.mapsettings.drawLayers.find(x => x.id === this.AOLAYER_ID);

                if (layer.geometries) {
                    const geometries = layer.geometries.filter(x => x.tag.beteckning === e.beteckning);
                    this.map.highlightGeometries(layer.id, geometries);
                }
            } else {
                this.map?.clearAllHighlights();
            }
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(MapEvents.geometriMenuHover, (geometry: IMapGeometry) => {
            if (geometry) {
                this.map.highlightGeometries(this.DPLAYER_ID, [geometry]);
            } else {
                this.map?.clearAllHighlights();
            }
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(MapEvents.geometriMenuSelected, (geometry: IMapGeometry) => {
            if (geometry) {
                this.map.selectGeometries(this.DPLAYER_ID, [geometry.id]);
                this.clickDrivningspunkt(geometry);
            } else {
                this.map.deselectGeometries(this.DPLAYER_ID, [this.selectedGeometry.id]);
            }
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(ListEvents.mouseOverAtgard, (payload: any) => {
            const geometry = this.mapsettings.drawLayers.find(l => l.id === this.ATGLAYER_ID)?.geometries?.find(g => g.tag?.atgard?.guid === payload.guid);
            if (geometry && payload.isMouseover) {
                this.map.highlightGeometries(this.ATGLAYER_ID, [geometry]);
            } else {
                this.map?.clearAllHighlights();
            }
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(ListEvents.select, (payload: any) => {
            const geometry = this.mapsettings.drawLayers.find(x => x.id === this.AOLAYER_ID).geometries?.find(x => x.tag.beteckning === payload.beteckning);
            if (payload.selected) {
                this.map.selectGeometries(this.AOLAYER_ID, [geometry.id], false);
            } else {
                this.map.deselectGeometries(this.AOLAYER_ID, [geometry.id], false);
            }
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(ListEvents.selectAll, (e) => {
            const ids = this.mapsettings.drawLayers.find(x => x.id === this.AOLAYER_ID)?.geometries?.map(x => x.id);
            this.map.selectGeometries(this.AOLAYER_ID, ids, false);
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe<ArbetsorderWrapper>(MapEvents.selectArbetsorder, ao => {
            // Stänger eventuella popups som var öppna på en förgående AO
            if (this.map) {
                this.map.closePopups();
            }
            // Sätter nya AOn
            this.selectArbetsorder(ao);
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe<any[]>(MapEvents.selectArbetsordrar, idlist => {
            this.selectArbetsordrar(idlist);
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe<IArbetsorderAtgarder[]>(MapEvents.setArbetsorderList, aolist => {
            this.arbetsordrar = aolist;
            this.defaultExtent = null;
            this.renderGeometries();
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe<IArbetsorderDetail>(MapEvents.arbetsorderAfterSave, ao => {
            this.map.closePopups();
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(MapEvents.geometriUpdated, () => {
            requestAnimationFrame(() => {
                if (this.selectedArbetsorder) {
                    this.renderDrivpunkter(this.selectedArbetsorder);
                }
            });
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe<number>(MapEvents.arbetsorderDeleted, arbetsorderId => {
            if (arbetsorderId && this.arbetsordrar && this.arbetsordrar.length > 0) {
                const foundIndex = this.arbetsordrar.findIndex(ao => ao.id === arbetsorderId);
                this.arbetsordrar.splice(foundIndex, 1);
                this.renderGeometries();
            }
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe<boolean>(MapEvents.showOnlySelected, (value: boolean) => {
            this.filterToSelected(value);
        }));
    }

    private renderArbetsordrar(arbetsordrar: any[], justRender = false) {
        clearTimeout(this.throttle);
        this.throttle = setTimeout(() => {
            if (arbetsordrar && this.map) {
                const geometries = arbetsordrar.map(ao => this.generateArbetsorderGeometry(ao));
                this.mapsettings.drawLayers.find(x => x.id === this.AOLAYER_ID).geometries = geometries;
                this.refreshLayer(this.AOLAYER_ID);
                if (!justRender) {
                    this.renderAtgarder(arbetsordrar);
                    this.gotoArbetsordrar();
                }
            }
        }, 50);
    }

    private renderAtgarder(arbetsordrar: any[]) {
        const atgarder = [];
        if (arbetsordrar && this.map) {
            arbetsordrar.forEach(ao => {
                const atgs = ao.atgarder.map(atg => this.getAtgard(atg, ao));
                atgarder.push(...atgs);
            });
        }
        const geometries = atgarder.map(atg => ({
            id: atg.id,
            northing: atg.northing,
            easting: atg.easting,
            label: atg.namn,
            color: atg?.ao?.id === this.selectedArbetsorder?.id ? "cyan" : [
                182,
                49,
                222
            ],
            size: 6,
            tag: {
                atgard: atg.raw,
                arbetsorder: atg.ao
            },
            selected: false,
            visible: true
        }));
        this.mapsettings.drawLayers.find(x => x.id === this.ATGLAYER_ID).geometries = geometries;
        this.refresh.next({ layerId: this.ATGLAYER_ID });
    }

    private renderDrivpunkter(ao: IArbetsorderDetail) {
        const geometries = (ao ? ao.kartgeometrier.filter(k => this.filterAvlaggKojplatsLarmpunkt(k)) : []).map((k) => ({
            wkt: k.geometri,
            id: k.id,
            label: this.getDrivningspunktLabel(k),
            color: "cyan",
            visible: true,
            tag: k,
            iconid: this.getDrivningspunktIcon(k)
        }));
        this.mapsettings.drawLayers.find(l => l.id === this.DPLAYER_ID).geometries = geometries;
        this.refreshLayer(this.DPLAYER_ID);
    }

    private renderGeometries() {
        this.renderArbetsordrar(this.arbetsordrar);
        this.map?.hideDrawLayer(this.DPLAYER_ID);

        if (!ObjectUtils.isNullOrUndefined(this.selectedArbetsorder)) {
            this.filterDestineringsomradeHelgrot();
            this.filterAtgarderUrval(this.selectedArbetsorder?.guid);
            this.filterArbetsorderUrval(this.selectedArbetsorder?.guid);
            this.filterAtgarderOnArbetsorderStatus(this.filterArbetsorderStatus, this.selectedArbetsorder?.guid);
            this.map?.showDrawLayer(this.DPLAYER_ID);
        } else {
            this.filterDestineringsomradeHelgrot();
            this.filterAtgarderUrval();
            this.filterArbetsorderUrval();
            this.filterAtgarderOnArbetsorderStatus(this.filterArbetsorderStatus);
        }
    }

    private selectArbetsorder(ao: ArbetsorderWrapper) {
        this.selectedArbetsorder = ao?.arbetsorder;
        this.selectedMetadata = ao?.metadata;
        this.addSelectedArbetsorderToArbetsorderList();

        this.renderGeometries();
        this.setupExtent();

        this.toggleDrivningspunkterLayerVisible();

        if (ao?.arbetsorder === null) {
            this.clearDrivningspunkter();
        }
        if (this.mapReady) {
            this.renderDrivpunkter(ao?.arbetsorder);
            this.refreshLayer(this.AOLAYER_ID);
            this.refreshLayer(this.ATGLAYER_ID);
            this.gotoArbetsordrar();
        }

        this.setCurrentAoExtent();
    }

    private selectArbetsordrar(selectedidlist: any[] = null) {
        if (ObjectUtils.hasValue(selectedidlist)) {
            setTimeout(() => {
                this.map.selectGeometries(this.AOLAYER_ID, selectedidlist, false);
                this.refreshLayer(this.AOLAYER_ID);
            }, 200);
        }
    }

    private setCurrentAoExtent(): void {
        const ext = this.selectedArbetsorder?.extent;
        if (ext) {
            this.currentAoExtent = new MapExtent(ext.minX,
                ext.minY,
                ext.maxX,
                ext.maxY);
        } else {
            this.currentAoExtent = null;
        }
    }

    private toggleDrivningspunkterLayerVisible() {
        const visible = !!this.selectedArbetsorder;
        this.mapsettings.drawLayers.find(l => l.id === this.DPLAYER_ID).visible = visible;
        this.refreshLayer(this.DPLAYER_ID);
    }
}
